import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import { Card, Table } from '@avila-tek/ui/src';
import { Hit } from '@algolia/client-search';
import { PageInfo, Pagination, PromoCode } from '../../models';
import { ActiveDotCell } from '../shared';
import { saleTypeTranslate } from '../../static';
import { useAlgoliaSearch } from '../../hooks';
import { AlgoliaHitToObject } from '../../lib';

const columnHelper = createColumnHelper<PromoCode>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Nombre',
  }),

  columnHelper.accessor('type', {
    header: 'Tipo de descuento',
    cell: (info) => saleTypeTranslate[info.getValue()],
  }),

  columnHelper.accessor('value', {
    header: 'Modificador',
  }),

  columnHelper.accessor('active', {
    header: 'Estatus',
    cell: (info) => <ActiveDotCell active={info?.getValue()} />,
  }),
];

interface SaleTableProps {
  data: Pagination<PromoCode>;
  pagination: Partial<PageInfo>;
  setPagination: React.Dispatch<React.SetStateAction<Partial<PageInfo>>>;
}

function SaleTable({ data, pagination, setPagination }: SaleTableProps) {
  const router = useRouter();

  const [searchText, setSearchText] = React.useState<string | number>('');
  const [salesData, setSalesData] = React.useState(data.items);
  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const { fetchData } = useAlgoliaSearch<PromoCode>({
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: ['name', 'type', 'value', 'code'],
    index: 'promoCodes',
    filters: '',
  });

  React.useEffect(() => {
    async function syncData() {
      if (searchText) {
        const { pageInfo, items } = await fetchData({
          searchText: searchText.toString(),
          newPage: pageInfoAlgolia.currentPage,
        });
        setSalesData(AlgoliaHitToObject<PromoCode>(items as Hit<PromoCode>[]));
        setPageInfoAlgolia(pageInfo);
      } else {
        setSalesData(data.items);
      }
    }
    syncData();
  }, [pageInfoAlgolia?.currentPage, searchText, pagination]);

  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const { id } = e.currentTarget.dataset;
    router.push(`/app/maintenance/sales/${id}`);
  };

  const onClickAddButton = () => router.push('/app/maintenance/sales/create');

  const handleGoToPage = (value: number) => {
    if (searchText) {
      setPageInfoAlgolia({ ...pageInfoAlgolia, currentPage: value - 1 });
    } else {
      setPagination({ ...pagination, currentPage: value });
    }
  };

  return (
    <Card className="overflow-x-auto p-5">
      <Table<PromoCode>
        data={salesData}
        columns={columns}
        href="/app/maintenance/sales/create"
        manualPagination
      >
        <Table.Header>
          <Table.SearchInput
            manualFilter
            value={searchText.toString()}
            onChange={(value) => {
              setSearchText(value);
            }}
          />
          <Table.AddButton title="Promoción" action={onClickAddButton} />
        </Table.Header>
        <Table.Content<PromoCode> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination
            handleGoToPage={handleGoToPage}
            paginationInfo={{
              count: data.count,
              pageInfo: searchText
                ? {
                    ...pageInfoAlgolia,
                    currentPage: pageInfoAlgolia.currentPage + 1,
                  }
                : data.pageInfo,
            }}
            className="ml-auto"
          />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default SaleTable;
